import { createSelector } from '@reduxjs/toolkit';
import { Banner } from '@ticketmaster/aurora';
import { useCallback } from 'react';
import { type InjectedIntl, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import messages from '../../../../messages';
import {
  selectCardPaymentEnabled,
  selectCurrentEventPaymentPermissions,
  selectManualCardEntryAllowed,
  selectManualCardEntryDisabled,
} from '../../checkout-selectors';
import { OtherForm } from '../../components/other-form/other-form';
import { CARD, OTHER, PIX } from '../../constants';
import EMVPaymentForm from '../emv-payment-form/emv-payment-form-connected';
import AuthorizationOffForm from './authorization-off-form';
import { authorizationOffValidator as initializeAuthorizationOffValidator } from './authorization-off-validator';
import StandardAuthorizationOnForm from './standard-authorization-on-form';
import './payment-form.scss';
import type { FieldNames } from '../../checkout-form';

type PaymentFormProps = {
  emvSupported: boolean;
  manualCardEntry: boolean;
  paymentMethod: string;
  authCodePlaceholder: string;
  cardFormLabels: {
    cardHolderLabel: string;
    cardNumberLabel: string;
    cardExpDateLabel: string;
  };
  otherFormLabels: {
    paymentInfoLabel: string;
    otherPaymentsLabel: string;
  };
  fieldNames: FieldNames;
  cashToTender: {
    amount: number;
    currency: string;
  };
  cashFormAutoSelect: boolean;
  intl: InjectedIntl;
};

const PaymentFormComponent = (props: PaymentFormProps) => {
  const initValidator = useCallback(() => initializeAuthorizationOffValidator(props.intl), [props.intl]);
  const authorizationOffValidator = useCallback(
    (value: string | null) => initValidator().validateCode(value),
    [initValidator],
  );

  const {
    authCodePlaceholder,
    paymentMethod,
    manualCardEntry,
    cardFormLabels,
    otherFormLabels,
    fieldNames,
    emvSupported,
  } = props;

  const manualCardEntryDisabled = useSelector(selectManualCardEntryDisabled);

  const manualCardEntryAllowed = useSelector(selectManualCardEntryAllowed);

  const cardPaymentDisabled = useSelector(
    createSelector(selectCardPaymentEnabled, (enabled: ReturnType<typeof selectCardPaymentEnabled>) => !enabled),
  );

  const isAuthorizationOn = useSelector(
    createSelector(
      selectCurrentEventPaymentPermissions,
      (paymentPermissions: ReturnType<typeof selectCurrentEventPaymentPermissions>) =>
        paymentPermissions?.mustAuthorizeCards || false,
    ),
  );

  const manualCardEntryProps = {
    checked: manualCardEntry,
    disabled: manualCardEntryDisabled,
    allowed: manualCardEntryAllowed,
    fieldNames,
    emvSupported,
  };

  if (paymentMethod === CARD) {
    if (cardPaymentDisabled) {
      return (
        <div role="alert">
          <Banner heading={props.intl.formatMessage(messages.cardPaymentNotConfigured)} isOpen variant="error" />
        </div>
      );
    }

    if (isAuthorizationOn) {
      if (emvSupported) {
        return <EMVPaymentForm manualCardEntry={manualCardEntryProps} showManualCardEntry />;
      }
      return (
        <StandardAuthorizationOnForm
          fieldNames={fieldNames}
          cardFormLabels={cardFormLabels}
          manualCardEntry={manualCardEntryProps}
        />
      );
    }

    return (
      <AuthorizationOffForm
        fieldNames={fieldNames}
        cardFormLabels={cardFormLabels}
        manualCardEntry={manualCardEntryProps}
        placeholder={authCodePlaceholder}
        validator={authorizationOffValidator}
      />
    );
  }

  if (paymentMethod === PIX) {
    return <EMVPaymentForm manualCardEntry={manualCardEntryProps} showManualCardEntry={false} />;
  }

  if (paymentMethod === OTHER) {
    return <OtherForm {...otherFormLabels} fieldNames={fieldNames} cashToTender={props.cashToTender} />;
  }
  return null;
};

export const PaymentForm = injectIntl(PaymentFormComponent);
